import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import ReactQuill from "react-quill";
import Select, { type SingleValue } from "react-select";
import {
  ArrowLeft,
  CalendarClock,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Save,
} from "lucide-react";
import { toast } from "react-toastify";
import serviceMeetings from "@/services/serviceMeetings";
import ImageGeneric from "@/assets/images/lg.png";

type Person = {
  id: number;
  name?: string;
  last_name?: string;
  url_img?: string | { path?: string } | null;
};

type MeetingMinute = {
  id: number;
  held_at: string;
  responsible_id: number;
  content: string;
  responsible?: Person;
};

type FormState = { held_at: string; responsible_id: string; content: string };
type PersonOption = { value: string; label: string; person: Person };
type ReunionSummary = {
  id: number | string;
  date?: string;
  id_responsavel?: number | string;
  responsavel?: unknown;
  responsaveis?: unknown;
  protagonista?: unknown;
  integrantes?: unknown;
  criador?: unknown;
  user?: unknown;
};

const emptyForm: FormState = { held_at: "", responsible_id: "", content: "" };
const plainText = (value: string) =>
  value.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();
const fullName = (person?: Person) =>
  [person?.name, person?.last_name].filter(Boolean).join(" ") || "Responsável";
const avatar = (person?: Person) => {
  const image = person?.url_img;
  return (typeof image === "string" ? image : image?.path) || ImageGeneric;
};
const asPerson = (value: unknown): Person | null => {
  if (!value || typeof value !== "object") return null;

  const record = value as Record<string, unknown>;
  const nestedUser =
    record.user && typeof record.user === "object"
      ? (record.user as Record<string, unknown>)
      : record;
  const rawId = nestedUser.id ?? record.id ?? record.user_id ?? record.value;
  if (rawId === null || rawId === undefined || rawId === "") return null;

  const id = Number(rawId);
  if (!Number.isFinite(id)) return null;

  return { ...record, ...nestedUser, id } as Person;
};
const asPeople = (value: unknown): Person[] => {
  if (typeof value === "string") {
    try {
      return asPeople(JSON.parse(value));
    } catch {
      return [];
    }
  }
  if (Array.isArray(value)) {
    return value.map(asPerson).filter((person): person is Person => Boolean(person));
  }
  if (value && typeof value === "object" && "data" in value) {
    return asPeople((value as { data?: unknown }).data);
  }

  const person = asPerson(value);
  return person ? [person] : [];
};

export function AtaReuniao({ reunion }: { reunion: ReunionSummary }) {
  const queryClient = useQueryClient();
  const queryKey = ["reunion-minutes", reunion.id];
  const [mode, setMode] = useState<"list" | "view" | "form">("list");
  const [selected, setSelected] = useState<MeetingMinute | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const minutesQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await serviceMeetings.getMinutes(reunion.id);
      if (response?.status !== 200) throw new Error(response?.data?.msg || "Não foi possível carregar as atas.");
      return {
        minutes: (response.data?.data || []) as MeetingMinute[],
        responsibles: (response.data?.responsibles || []) as Person[],
      };
    },
  });

  const people = useMemo(() => {
    const map = new Map<number, Person>();

    const availablePeople = new Map(
      minutesQuery.data?.responsibles.map((person) => [
        Number(person.id),
        person,
      ]),
    );
    const addPerson = (value: unknown) => {
      const person = asPerson(value);
      if (!person) return;
      const personId = person.id;

      map.set(personId, { ...availablePeople.get(personId), ...person });
    };

    asPeople(reunion.integrantes).forEach(addPerson);
    asPeople(reunion.responsaveis).forEach(addPerson);
    addPerson(reunion.responsavel);
    addPerson(
      reunion.id_responsavel !== undefined &&
        reunion.id_responsavel !== null &&
        reunion.id_responsavel !== ""
        ? availablePeople.get(Number(reunion.id_responsavel))
        : undefined,
    );
    addPerson(reunion.protagonista);
    addPerson(reunion.criador);
    addPerson(reunion.user);

    return [...map.values()].sort((a, b) => fullName(a).localeCompare(fullName(b), "pt-BR"));
  }, [minutesQuery.data?.responsibles, reunion]);

  const peopleOptions = useMemo<PersonOption[]>(
    () =>
      people.map((person) => ({
        value: String(person.id),
        label: fullName(person),
        person,
      })),
    [people],
  );

  const selectedPersonOption =
    peopleOptions.find((option) => option.value === form.responsible_id) ?? null;
  const defaultResponsibleId =
    people.find(
      (person) =>
        Number(person.id) ===
        Number(
          reunion.id_responsavel ?? asPerson(reunion.responsavel)?.id,
        ),
    )?.id ?? people[0]?.id;

  useEffect(() => {
    if (mode === "form" && !form.responsible_id && people.length) {
      setForm((current) => ({
        ...current,
        responsible_id: String(defaultResponsibleId),
      }));
    }
  }, [defaultResponsibleId, form.responsible_id, mode, people.length]);

  const openCreate = () => {
    setSelected(null);
    setForm({
      held_at: moment(reunion.date || new Date()).format("YYYY-MM-DDTHH:mm"),
      responsible_id: String(defaultResponsibleId ?? ""),
      content: "",
    });
    setMode("form");
  };

  const openEdit = (minute: MeetingMinute) => {
    const responsibleId = String(minute.responsible_id);
    setSelected(minute);
    setForm({
      held_at: moment(minute.held_at).format("YYYY-MM-DDTHH:mm"),
      responsible_id: peopleOptions.some(
        (option) => option.value === responsibleId,
      )
        ? responsibleId
        : "",
      content: minute.content,
    });
    setMode("form");
  };

  const save = async () => {
    const hasEligibleResponsible = peopleOptions.some(
      (option) => option.value === form.responsible_id,
    );
    if (!form.held_at || !hasEligibleResponsible || !plainText(form.content)) {
      toast.warning("Preencha data e hora, responsável e descrição da ata.");
      return;
    }
    setSaving(true);
    const payload = {
      held_at: moment(form.held_at).toISOString(),
      responsible_id: Number(form.responsible_id),
      content: form.content,
    };
    const response = selected
      ? await serviceMeetings.updateMinute(reunion.id, selected.id, payload)
      : await serviceMeetings.createMinute(reunion.id, payload);
    setSaving(false);
    if (![200, 201].includes(response?.status)) {
      toast.error(response?.data?.msg || "Não foi possível salvar a ata.");
      return;
    }
    toast.success(response.data?.msg || "Ata salva com sucesso.");
    await queryClient.invalidateQueries({ queryKey });
    setSelected(response.data.data);
    setMode("view");
  };

  if (minutesQuery.isLoading) {
    return <div className="flex min-h-64 items-center justify-center text-cinza-500"><Loader2 className="mr-2 animate-spin" size={20} /> Carregando atas...</div>;
  }

  if (minutesQuery.isError) {
    return <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-center text-sm text-red-600">Não foi possível carregar as atas. <button className="ml-1 underline" onClick={() => minutesQuery.refetch()}>Tentar novamente</button></div>;
  }

  if (mode === "form") {
    return (
      <section className="rounded-3xl border border-cinza-100 bg-[#F8F9FB] p-4 sm:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={() => setMode(selected ? "view" : "list")} className="flex items-center gap-2 text-sm text-cinza-600 hover:text-azul-400"><ArrowLeft size={17} /> Voltar</button>
          <h3 className="text-lg font-semibold text-cinza-800">{selected ? "Editar ata" : "Nova ata de reunião"}</h3>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-xs font-medium text-cinza-600">Data e hora da reunião
            <input type="datetime-local" value={form.held_at} onChange={(event) => setForm({ ...form, held_at: event.target.value })} className="mt-1.5 w-full rounded-xl border border-cinza-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-azul-400" />
          </label>
          <label className="text-xs font-medium text-cinza-600">
            Responsável pela ata
            <Select<PersonOption, false>
              value={selectedPersonOption}
              options={peopleOptions}
              isLoading={minutesQuery.isFetching}
              onChange={(option: SingleValue<PersonOption>) =>
                setForm({ ...form, responsible_id: option?.value || "" })
              }
              placeholder="Selecione uma pessoa"
              noOptionsMessage={() => "Nenhuma pessoa encontrada"}
              className="mt-1.5 text-sm font-normal"
              classNamePrefix="meeting-minute-person"
              formatOptionLabel={(option) => (
                <div className="flex min-w-0 items-center gap-2.5">
                  <img
                    src={avatar(option.person)}
                    alt=""
                    className="h-7 w-7 shrink-0 rounded-full object-cover"
                  />
                  <span className="truncate">{option.label}</span>
                </div>
              )}
              styles={{
                control: (base, state) => ({
                  ...base,
                  minHeight: 42,
                  borderRadius: 12,
                  borderColor: state.isFocused ? "#153DCE" : "#D9D9D9",
                  boxShadow: "none",
                  "&:hover": { borderColor: "#153DCE" },
                }),
                menu: (base) => ({ ...base, zIndex: 40 }),
                option: (base) => ({ ...base, padding: "8px 12px" }),
              }}
            />
          </label>
        </div>
        <div className="mt-4 rounded-2xl bg-white">
          <ReactQuill theme="snow" value={form.content} onChange={(content) => setForm({ ...form, content })} placeholder="Registre o resumo, decisões, responsáveis e próximos passos..." modules={{ toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]] }} className="min-h-[260px] [&_.ql-container]:min-h-[210px] [&_.ql-editor]:min-h-[210px]" />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => setMode(selected ? "view" : "list")} className="rounded-full border border-cinza-200 px-5 py-2 text-sm text-cinza-600">Cancelar</button>
          <button type="button" disabled={saving} onClick={save} className="flex items-center gap-2 rounded-full bg-azul-400 px-5 py-2 text-sm font-medium text-white disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Salvar ata</button>
        </div>
      </section>
    );
  }

  if (mode === "view" && selected) {
    return (
      <section className="rounded-3xl border border-cinza-100 bg-white p-4 sm:p-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={() => setMode("list")} className="flex items-center gap-2 text-sm text-cinza-600 hover:text-azul-400"><ArrowLeft size={17} /> Todas as atas</button>
          <button type="button" onClick={() => openEdit(selected)} className="flex items-center gap-2 rounded-full border border-azul-400 px-4 py-2 text-sm text-azul-400"><Pencil size={15} /> Editar</button>
        </div>
        <div className="mb-6 grid gap-3 rounded-2xl bg-[#F5F7FB] p-4 sm:grid-cols-2">
          <div className="flex items-center gap-3"><CalendarClock className="text-azul-400" size={20} /><div><p className="text-[11px] text-cinza-400">Reunião realizada em</p><p className="text-sm font-medium">{moment(selected.held_at).format("DD/MM/YYYY [às] HH:mm")}</p></div></div>
          <div className="flex items-center gap-3"><img src={avatar(selected.responsible)} className="h-9 w-9 rounded-full object-cover" alt="" /><div><p className="text-[11px] text-cinza-400">Responsável pela ata</p><p className="text-sm font-medium">{fullName(selected.responsible)}</p></div></div>
        </div>
        <ReactQuill theme="bubble" readOnly value={selected.content} modules={{ toolbar: false }} className="prose max-w-none [&_.ql-editor]:min-h-[180px] [&_.ql-editor]:p-0" />
      </section>
    );
  }

  const minutes = minutesQuery.data?.minutes || [];
  return (
    <section className="rounded-3xl border border-cinza-100 bg-[#F8F9FB] p-4 sm:p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3"><span className="rounded-xl bg-white p-2 text-azul-400 shadow-sm"><FileText size={20} /></span><div><h3 className="font-semibold text-cinza-800">Atas da reunião</h3><p className="text-xs text-cinza-400">Decisões, registros e próximos passos</p></div></div>
        <button type="button" onClick={openCreate} className="flex items-center gap-2 rounded-full bg-azul-400 px-4 py-2 text-sm font-medium text-white"><Plus size={16} /> Nova ata</button>
      </div>
      {minutes.length === 0 ? (
        <div className="flex min-h-56 flex-col items-center justify-center rounded-2xl border border-dashed border-cinza-200 bg-white p-8 text-center"><FileText className="mb-3 text-cinza-300" size={36} /><p className="font-medium text-cinza-700">Nenhuma ata registrada</p><p className="mt-1 max-w-sm text-sm text-cinza-400">Crie a primeira ata para centralizar o resumo, as decisões e os próximos passos desta reunião.</p></div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {minutes.map((minute) => <button key={minute.id} type="button" onClick={() => { setSelected(minute); setMode("view"); }} className="group rounded-2xl border border-cinza-100 bg-white p-4 text-left transition hover:border-azul-300 hover:shadow-sm"><div className="flex items-start justify-between gap-3"><div className="flex items-center gap-2 text-sm font-semibold text-cinza-800"><CalendarClock size={17} className="text-azul-400" />{moment(minute.held_at).format("DD/MM/YYYY [às] HH:mm")}</div><Pencil size={14} className="text-cinza-300 group-hover:text-azul-400" /></div><p className="mt-3 line-clamp-2 text-sm leading-relaxed text-cinza-500">{plainText(minute.content)}</p><div className="mt-4 flex items-center gap-2 border-t border-cinza-100 pt-3"><img src={avatar(minute.responsible)} alt="" className="h-6 w-6 rounded-full object-cover" /><span className="text-xs text-cinza-500">{fullName(minute.responsible)}</span></div></button>)}
        </div>
      )}
    </section>
  );
}
